import { SuspendUserSchema, type SuspendUserRequest } from '@aysar/contracts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { type ModalComponentProps } from '@/shared/components/Modals/modal-registry/modal-registry.types';
import { DynamicFormModal } from '@/shared/components/dynamic-form/DynamicFormModal/DynamicFormModal';
import { useToast } from '@/shared/hooks/useToast/useToast';

import { useSuspendAccountMutation } from '@/shared/services/account-status/account-status.api';

import { buildSuspendFields } from '../config/account-actions.config';

/**
 * Suspending an account.
 *
 * A FORM, not a confirm dialog, and that is forced by the api rather than a
 * preference: `SuspendUserSchema` requires both an end date and a reason, and
 * the member is **shown** the reason. A yes/no box can collect neither, and a
 * reason typed carelessly into one is a reason somebody reads verbatim.
 *
 * The end date is mandatory by design on the server too — there is no such thing
 * as an indefinite suspension that quietly becomes a ban nobody decided on (N9).
 *
 * A kit dialog since P27c1: the staff page and the members page both open it by
 * its type string (`ACCOUNT_MODALS.SUSPEND`), and the write goes through the
 * shared `accountStatusApi`, whose announcement refreshes whichever list opened it.
 */
export function SuspendUserModal({ open, onCancel, modalData }: ModalComponentProps) {
  const { t } = useTranslation('uiKit');
  const toast = useToast();
  const [suspendUser] = useSuspendAccountMutation();

  const userId = typeof modalData?.['userId'] === 'string' ? modalData['userId'] : null;
  const email = typeof modalData?.['email'] === 'string' ? modalData['email'] : '';
  const fields = useMemo(() => buildSuspendFields(t), [t]);

  async function submit(values: SuspendUserRequest): Promise<void> {
    if (userId === null) {
      return;
    }

    await suspendUser({ id: userId, body: values }).unwrap();
    toast.success(t('account.suspend.success'));
    onCancel();
  }

  return (
    <DynamicFormModal<SuspendUserRequest>
      isOpen={open}
      onOpenChange={(next) => {
        if (!next) {
          onCancel();
        }
      }}
      title={t('account.suspend.title', { email })}
      description={t('account.suspend.description')}
      fields={fields}
      schema={SuspendUserSchema}
      submitLabel={t('account.suspend.submit')}
      onSubmit={submit}
    />
  );
}
